// Browser-side client for the Lumvio calls control plane.
//
// Participant auth tokens returned here stay in React memory only. They are
// never written to localStorage, the database, logs, Sentry or PostHog.
import { supabase } from "@/integrations/supabase/client";
import type { CallMode, CallRow } from "@/lib/calls.core";
import { stripProviderFields } from "@/lib/calls.core";

export interface CallActionResult<T = unknown> {
  ok: boolean;
  error_code?: string;
  retryable?: boolean;
  data?: T;
}

async function invoke<T>(body: Record<string, unknown>): Promise<CallActionResult<T>> {
  const { data, error } = await supabase.functions.invoke("project-calls", { body });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    if (ctx) {
      try {
        const parsed = (await ctx.json()) as { error_code?: string; retryable?: boolean };
        return { ok: false, error_code: parsed.error_code ?? "REQUEST_FAILED", retryable: parsed.retryable };
      } catch {
        /* fall through */
      }
    }
    return { ok: false, error_code: "REQUEST_FAILED", retryable: true };
  }
  const payload = data as { ok?: boolean; error_code?: string } & Record<string, unknown>;
  if (payload?.ok === false) return { ok: false, error_code: payload.error_code ?? "REQUEST_FAILED" };
  return { ok: true, data: payload as T };
}

export async function fetchCallsConfig() {
  return invoke<{ enabled: boolean }>({ action: "config" });
}

export async function startCall(input: {
  projectId: string;
  mode: CallMode;
  requestId: string;
}) {
  return invoke<{ call: CallRow; reused: boolean }>({
    action: "start_call",
    project_id: input.projectId,
    mode: input.mode,
    request_id: input.requestId,
  });
}

export async function acceptCall(callId: string) {
  return invoke<{ call: CallRow }>({ action: "accept_call", call_id: callId });
}

export async function declineCall(callId: string) {
  return invoke<{ call: CallRow }>({ action: "decline_call", call_id: callId });
}

export async function cancelCall(callId: string) {
  return invoke<{ call: CallRow }>({ action: "cancel_call", call_id: callId });
}

export async function joinCall(callId: string) {
  return invoke<{ auth_token: string; mode: CallMode; call: CallRow }>({
    action: "join_call",
    call_id: callId,
  });
}

export async function refreshCallToken(callId: string) {
  return invoke<{ auth_token: string; mode: CallMode }>({
    action: "refresh_participant_token",
    call_id: callId,
  });
}

export async function endCall(callId: string) {
  return invoke<{ call: CallRow; media_terminated: boolean }>({
    action: "end_call",
    call_id: callId,
  });
}

export async function fetchCallHistory(projectId: string) {
  const res = await invoke<{ calls: Array<Record<string, unknown>> }>({
    action: "history",
    project_id: projectId,
  });
  if (!res.ok || !res.data) return res;
  return {
    ...res,
    data: { calls: res.data.calls.map((c) => stripProviderFields(c)) as unknown as CallRow[] },
  };
}

/** Map an error_code to a translation key under `calls.errors`. */
export function callErrorKey(code: string | undefined): string {
  switch (code) {
    case "USER_ALREADY_IN_CALL":
      return "calls.errors.userBusy";
    case "PROJECT_ALREADY_IN_CALL":
      return "calls.errors.projectBusy";
    case "CALL_UNAVAILABLE_NO_CLIENT_ACCOUNT":
      return "calls.errors.noClientAccount";
    case "CALL_EXPIRED":
      return "calls.errors.expired";
    case "FEATURE_DISABLED":
      return "calls.errors.disabled";
    case "PRESET_NOT_READY":
    case "CALLS_NOT_CONFIGURED":
      return "calls.errors.notConfigured";
    case "PROVIDER_UNAVAILABLE":
      return "calls.errors.providerUnavailable";
    case "FORBIDDEN":
      return "calls.errors.forbidden";
    default:
      return "calls.errors.generic";
  }
}
